'use client';

import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { computeProjection, summarize } from '@/lib/learn/compounding';
import { cn } from '@/lib/cn';

const YEAR_OPTIONS = [5, 10, 15, 20, 25, 30] as const;
type Years = typeof YEAR_OPTIONS[number];

const RISK_PRESETS = [
  { key: 'conservative', label: 'Conservative', rate: 0.05 },
  { key: 'balanced', label: 'Balanced', rate: 0.07 },
  { key: 'aggressive', label: 'Aggressive', rate: 0.09 },
] as const;
type RiskKey = typeof RISK_PRESETS[number]['key'];

const BOND_RATE = 0.03;

function formatUSD(n: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatMultiple(n: number) {
  if (!isFinite(n) || isNaN(n)) return '—';
  return `${n.toFixed(1)}×`;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span
        className="block text-xs font-medium uppercase tracking-wider mb-2"
        style={{ color: 'var(--color-text-muted)' }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

interface TooltipPayload {
  color?: string;
  name?: string;
  value?: number;
}

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipPayload[]; label?: string | number }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs"
      style={{
        backgroundColor: 'var(--color-surface-secondary)',
        border: '1px solid var(--color-border)',
      }}
    >
      <p className="font-semibold text-[var(--color-text)] mb-1">Year {label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          <span style={{ color: 'var(--color-text-secondary)' }}>{p.name}: </span>
          <span className="font-semibold">{typeof p.value === 'number' ? formatUSD(p.value) : '—'}</span>
        </p>
      ))}
    </div>
  );
}

export function CompoundingCalculator() {
  const [initial, setInitial] = useState(1000);
  const [monthly, setMonthly] = useState(100);
  const [years, setYears] = useState<Years>(20);
  const [risk, setRisk] = useState<RiskKey>('balanced');
  const [inflationAdjusted, setInflationAdjusted] = useState(false);
  const [compare, setCompare] = useState(false);

  const annualReturn = RISK_PRESETS.find((r) => r.key === risk)?.rate ?? 0.07;

  const stocksProjection = useMemo(
    () => computeProjection({ initial, monthlyContribution: monthly, years, annualReturn, inflationAdjusted }),
    [initial, monthly, years, annualReturn, inflationAdjusted]
  );

  const bondsProjection = useMemo(
    () => computeProjection({ initial, monthlyContribution: monthly, years, annualReturn: BOND_RATE, inflationAdjusted }),
    [initial, monthly, years, inflationAdjusted]
  );

  const chartData = useMemo(
    () => stocksProjection.map((pt, i) => ({
      year: pt.year,
      stocks: pt.value,
      bonds: bondsProjection[i]?.value ?? null,
    })),
    [stocksProjection, bondsProjection]
  );

  const summary = summarize(stocksProjection);

  return (
    <section id="compounding" className="py-16 scroll-mt-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-10"
        >
          <span
            className="inline-block text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: 'var(--color-accent-muted)' }}
          >
            3 · Compounding
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-text)]">
            Time is your biggest advantage
          </h2>
          <p className="mt-3 text-lg" style={{ color: 'var(--color-text-secondary)' }}>
            Returns earn returns. Try it: plug in a starting amount and see what happens over the years.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Inputs */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 rounded-2xl p-6 border border-[var(--color-border-subtle)]"
            style={{ backgroundColor: 'var(--color-surface)' }}
          >
            <h3 className="text-lg font-semibold text-[var(--color-text)] mb-5">Your scenario</h3>

            <div className="space-y-5">
              <Field label="Initial investment">
                <div className="relative">
                  <span
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-sm"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    $
                  </span>
                  <input
                    type="number"
                    min={0}
                    value={initial}
                    onChange={(e) => setInitial(Math.max(0, Number(e.target.value)))}
                    className="w-full pl-7 pr-3 py-2.5 rounded-xl text-sm tabular-nums focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: 'var(--color-bg)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text)',
                    }}
                  />
                </div>
              </Field>

              <Field label="Monthly contribution">
                <div className="relative">
                  <span
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-sm"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    $
                  </span>
                  <input
                    type="number"
                    min={0}
                    value={monthly}
                    onChange={(e) => setMonthly(Math.max(0, Number(e.target.value)))}
                    className="w-full pl-7 pr-3 py-2.5 rounded-xl text-sm tabular-nums focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: 'var(--color-bg)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text)',
                    }}
                  />
                </div>
              </Field>

              <Field label="Time horizon">
                <div role="radiogroup" aria-label="Time horizon in years" className="grid grid-cols-6 gap-1.5">
                  {YEAR_OPTIONS.map((y) => (
                    <button
                      key={y}
                      type="button"
                      role="radio"
                      aria-checked={years === y}
                      onClick={() => setYears(y)}
                      className={cn(
                        'py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer'
                      )}
                      style={
                        years === y
                          ? { backgroundColor: 'var(--color-accent)', color: '#fff' }
                          : { backgroundColor: 'var(--color-bg)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }
                      }
                    >
                      {y}y
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Risk preset">
                <div role="radiogroup" aria-label="Risk preset" className="grid grid-cols-3 gap-1.5">
                  {RISK_PRESETS.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      role="radio"
                      aria-checked={risk === r.key}
                      onClick={() => setRisk(r.key)}
                      className={cn(
                        'py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer flex flex-col items-center'
                      )}
                      style={
                        risk === r.key
                          ? { backgroundColor: 'var(--color-accent)', color: '#fff' }
                          : { backgroundColor: 'var(--color-bg)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }
                      }
                    >
                      <span>{r.label}</span>
                      <span className="text-[10px] opacity-80">{(r.rate * 100).toFixed(0)}%</span>
                    </button>
                  ))}
                </div>
              </Field>

              <div className="space-y-3 pt-2" style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
                <ToggleRow
                  label="Show in today's dollars"
                  description="Subtract 2.5% inflation"
                  enabled={inflationAdjusted}
                  onChange={setInflationAdjusted}
                />
                <ToggleRow
                  label="Show stocks vs. bonds"
                  description="Overlay a 3% bond line"
                  enabled={compare}
                  onChange={setCompare}
                />
              </div>
            </div>
          </motion.div>

          {/* Chart + Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3 space-y-5"
          >
            <div
              className="rounded-2xl p-6 border border-[var(--color-border-subtle)]"
              style={{ backgroundColor: 'var(--color-surface)' }}
            >
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={16} style={{ color: 'var(--color-accent-muted)' }} />
                <h3 className="text-sm font-semibold text-[var(--color-text)]">
                  Projected value over {years} years
                </h3>
              </div>
              <div className="h-72 w-full" aria-label="Compounding growth chart">
                <ResponsiveContainer width="100%" height={288}>
                  <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
                    <XAxis
                      dataKey="year"
                      stroke="var(--color-text-muted)"
                      fontSize={11}
                      tickFormatter={(v) => `${v}y`}
                    />
                    <YAxis
                      stroke="var(--color-text-muted)"
                      fontSize={11}
                      tickFormatter={(v) => {
                        if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
                        if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
                        return `$${v}`;
                      }}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    {compare && <Legend wrapperStyle={{ fontSize: '0.75rem' }} />}
                    <Line
                      type="monotone"
                      dataKey="stocks"
                      stroke="#10b981"
                      strokeWidth={2.5}
                      dot={false}
                      name="Stocks"
                    />
                    {compare && (
                      <Line
                        type="monotone"
                        dataKey="bonds"
                        stroke="#3b82f6"
                        strokeWidth={2.5}
                        dot={false}
                        name="Bonds"
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <SummaryTile label="Final value" value={formatUSD(summary.finalValue)} emphasis />
              <SummaryTile label="Total contributed" value={formatUSD(summary.totalContributed)} />
              <SummaryTile
                label="Total growth"
                value={formatUSD(summary.totalGrowth)}
                valueColor={summary.totalGrowth >= 0 ? '#10b981' : '#ef4444'}
              />
              <SummaryTile label="Growth multiple" value={formatMultiple(summary.growthMultiple)} />
            </div>

            <p className="text-xs italic px-1" style={{ color: 'var(--color-text-muted)' }}>
              Hypothetical illustration using constant annual returns. Real markets vary year to year.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ToggleRow({
  label,
  description,
  enabled,
  onChange,
}: {
  label: string;
  description: string;
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => onChange(!enabled)}
      className="w-full flex items-center justify-between gap-3 py-2 text-left cursor-pointer"
    >
      <div>
        <p className="text-sm font-medium text-[var(--color-text)]">{label}</p>
        <p className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>{description}</p>
      </div>
      <span
        className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0"
        style={{
          backgroundColor: enabled
            ? 'var(--color-accent)'
            : 'var(--color-surface-hover)',
        }}
      >
        <span
          className="inline-block h-5 w-5 rounded-full bg-white transform transition-transform"
          style={{ transform: enabled ? 'translateX(22px)' : 'translateX(2px)' }}
        />
      </span>
    </button>
  );
}

function SummaryTile({
  label,
  value,
  emphasis,
  valueColor,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  valueColor?: string;
}) {
  return (
    <div
      className={cn('rounded-xl p-4 border border-[var(--color-border-subtle)]')}
      style={{
        backgroundColor: emphasis
          ? 'color-mix(in srgb, var(--color-accent) 10%, var(--color-surface))'
          : 'var(--color-surface)',
      }}
    >
      <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
        {label}
      </p>
      <p
        className="text-xl sm:text-2xl font-bold mt-1 tabular-nums"
        style={{ color: valueColor ?? 'var(--color-text)' }}
      >
        {value}
      </p>
    </div>
  );
}
